import { useRef } from "react";
import { useControl } from "react-map-gl/mapbox";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import { usePolygonDrawContext } from "./PolygonDrawContext";
import { DRAW_MODES } from "./helpers";
import { DRAW_STYLES } from "./drawStyles";

// Minimal read-only mode: renders features but ignores every pointer/keyboard
// interaction, so polygons can't be selected, moved, or edited while an
// assessment result is displayed.
const StaticMode = {
  onSetup() {
    this.setActionableState();
    return {};
  },
  toDisplayFeatures(state, geojson, display) {
    display(geojson);
  },
};

/**
 * Adds a MapboxDraw instance to the map (no default UI — we drive it from
 * <PolygonToolbar>) and wires draw events into the polygon-draw context.
 * Must be rendered as a child of react-map-gl's <Map>.
 */
export default function DrawControl() {
  const {
    registerDraw,
    unregisterDraw,
    onDrawChange,
    onModeChange,
    onSelectionChange,
  } = usePolygonDrawContext();

  const drawInstanceRef = useRef(null);

  useControl(
    () => {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        defaultMode: DRAW_MODES.SELECT,
        styles: DRAW_STYLES,
        modes: { ...MapboxDraw.modes, [DRAW_MODES.STATIC]: StaticMode },
      });
      drawInstanceRef.current = draw;
      return draw;
    },
    ({ map }) => {
      registerDraw(drawInstanceRef.current);
      map.on("draw.create", onDrawChange);
      map.on("draw.update", onDrawChange);
      map.on("draw.delete", onDrawChange);
      map.on("draw.modechange", onModeChange);
      map.on("draw.selectionchange", onSelectionChange);
    },
    ({ map }) => {
      map.off("draw.create", onDrawChange);
      map.off("draw.update", onDrawChange);
      map.off("draw.delete", onDrawChange);
      map.off("draw.modechange", onModeChange);
      map.off("draw.selectionchange", onSelectionChange);
      unregisterDraw();
    },
  );

  return null;
}
